import { notify } from '@tiger/bugsnag';
import { clearCache, loadCG } from '@tiger/cg';
import axios, { AxiosPromise, AxiosRequestConfig } from 'axios';
import 'utils/http';
import UI from 'stores/ui';
import { urlQuery } from '../constants';
// 导入接口定义
import * as ACCOUNT from './apis/ACCOUNT';
import * as CG from './apis/CG';
import * as CUSTOMER from './apis/CUSTOMER';
import * as ESOP from './apis/ESOP';
import * as INFOBASE from './apis/INFOBASE';
import * as OAUTH from './apis/OAUTH';
import * as OAUTH_USER from './apis/OAUTH_USER';
import * as PORTAL from './apis/PORTAL';
import * as REPORT from './apis/REPORT';
import * as SUPPORT from './apis/SUPPORT';
import * as TESSERACT from './apis/TESSERACT';

export const APICollection = {
    OAUTH,
    PORTAL,
    CG,
    CUSTOMER,
    TESSERACT,
    ESOP,
    SUPPORT,
    OAUTH_USER,
    INFOBASE,
    REPORT,
    ACCOUNT
};

interface APIInstance {
    (...args: Array<string | number>): string;

    request<T = any>(config: AxiosRequestConfig, ...args: Array<string | number>): AxiosPromise<T>;
    get<T = any>(...args: Array<string | number>): AxiosPromise<T>;
    get<T = any>(config?: AxiosRequestConfig, ...args: Array<string | number>): AxiosPromise<T>;
    delete(config?: AxiosRequestConfig, ...args: Array<string | number>): AxiosPromise;
    head(config?: AxiosRequestConfig, ...args: Array<string | number>): AxiosPromise;
    post<T = any>(data?: any, config?: AxiosRequestConfig, ...args: Array<string | number>): AxiosPromise<T>;
    put<T = any>(data?: any, config?: AxiosRequestConfig, ...args: Array<string | number>): AxiosPromise<T>;
    patch<T = any>(data?: any, config?: AxiosRequestConfig, ...args: Array<string | number>): AxiosPromise<T>;
}

type APIPicker<T extends { [P: string]: any }> = {
    [P in keyof T]: API<T[P]['API']>;
};
type API<T> = { [P in keyof T]: T[P] extends object ? API<T[P]> : APIInstance };

export const isDev = process.env.NODE_ENV === 'development' || process.env.REACT_APP_ENV === 'development';

export const isProd = process.env.NODE_ENV === 'production';

export const isQA = process.env.TIGER_QA_ENV === 'true';

export const axiosMethods = ['request', 'get', 'post', 'put', 'delete', 'head', 'patch'];

export default Object.keys(APICollection).reduce((exports, host) => {
    exports[host] = enhanced(APICollection[host]);

    return exports;
}, {}) as APIPicker<typeof APICollection>;

function enhanced(config) {
    const createAPI = apis =>
        Object.keys(apis).reduce((result, key) => {
            const pathname = apis[key];

            function apiInstance(...args) {
                let index = 0;
                const host = isDev ? (isQA && config.HOST[2]) || config.HOST[0] : config.HOST[1];

                return (
                    host +
                    pathname.replace(/:[^/]+/gi, match => {
                        const arg = args[index++];

                        return arg === undefined ? match : arg;
                    })
                );
            }

            axiosMethods.forEach(type => {
                apiInstance[type] = (...args) => {
                    const apiArgs: Array<string | number> = [];
                    const httpArgs: object[] = [];

                    args.forEach(item => {
                        if (typeof item === 'string' || typeof item === 'number') {
                            apiArgs.push(item);
                        } else {
                            httpArgs.push(item);
                        }
                    });

                    // 针对request方法特殊处理
                    if (type === 'request') {
                        return axios.request({
                            ...httpArgs[0],
                            url: apiInstance(...apiArgs)
                        });
                    }

                    return axios[type](apiInstance(...apiArgs), ...httpArgs);
                };
            });

            result[key] = typeof pathname === 'string' ? apiInstance : createAPI(pathname);

            return result;
        }, {});

    return createAPI(config.API);
}

/**
 * 根据配置的HOST_ALIAS切换host
 */
export function setAlias(name: string) {
    Object.keys(APICollection).forEach(host => {
        const config = APICollection[host];

        if (config.HOST_ALIAS && name in config.HOST_ALIAS) {
            config.HOST.splice(0, 2, ...config.HOST_ALIAS[name]);
        }
    });
}

// 暂存测试环境
const API_ENV_CACHE_KEY = 'QAX';

export function getAPIEnv() {
    // 线上暂时不接入，目前仅有一套域名
    return __DEV__ ? (urlQuery._QAX as string) || localStorage.getItem(API_ENV_CACHE_KEY) || undefined : undefined;
}

export function saveAPIEnv(env: string) {
    localStorage.setItem(API_ENV_CACHE_KEY, env);
    clearCache();
}

export function removeAPIEnv() {
    localStorage.removeItem(API_ENV_CACHE_KEY);
    clearCache();
}

export async function preloadCG() {
    try {
        // 测试环境读取浏览器的env，并实时拉取对应的cg配置
        // 线上暂时不接入，目前仅有一套域名
        const environment = getAPIEnv();

        if (__DEV__ && (UI.isTiger || environment)) {
            const cgData = await loadCG({
                fallback: true,
                environment,
                appName: 'TradeUP',
                tenantId: 'TBUS'
            });

            if (cgData) {
                Object.assign(CG_API, cgData);

                Object.keys(APICollection).forEach(host => {
                    APICollection[host].bindCG?.();
                });
            }
        }
    } catch (err) {
        notify(err);
    }
}
